import 'twin.macro';
import 'styled-components/macro';

import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache } from '@apollo/client';
import { gql } from '@apollo/client/core';
import * as Sentry from '@sentry/react';
import { SentryLink } from 'apollo-link-sentry';
import { Suspense, lazy, useEffect } from 'react';
import * as React from 'react';
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { CompatRoute, CompatRouter } from 'react-router-dom-v5-compat';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { NotificationProvider } from 'core/context/Notifications';
import { FeatureFlagManager } from 'core/molecules/feature-flag-manager/FeatureFlagManager';
import { Logout } from 'core/pages/logout/Logout';
import { StudyRoomLinkRedirect } from 'core/pages/study-room-link-redirect/StudyRoomLinkRedirect';
import { SkilljarContextProvider } from 'play/support/skilljar';

import { ScrollToTop } from './component-lib/atoms/ScrollToTop';
import ThemeWrapper from './component-lib/ThemeWrapper';
import { AuthenticationContextProvider, useAuthenticationContext } from './core/context/Authentication';
import * as auth from './support/firebase/auth';
import { trackAnonUser, trackPageView, trackUser } from './support/tracking';
import ToyApp from './toy/ToyApp';
import { UserSegment } from './types/graphql';

const ArcadeApp = lazy(() => import('./arcade/ArcadeApp'));
const CoreApp = lazy(() => import('./core/CoreApp'));
const PlayApp = lazy(() => import('./play/PlayApp'));
const CreateApp = lazy(() => import('./create/CreateApp'));
const ClassroomApp = lazy(() => import('./classroom/ClassroomApp'));

export function App() {
  return (
    <Providers>
      <ScrollToTop />
      <AppContainer>
        <Router />
        <FeatureFlagManager />
      </AppContainer>
    </Providers>
  );
}

function Providers({ children }: { children: React.ReactNode }) {
  return (
    <BrowserRouter>
      <CompatRouter>
        <ThemeWrapper>
          <ApolloProvider client={client}>
            <AuthenticationContextProvider>
              <NotificationProvider>
                <SkilljarContextProvider>{children}</SkilljarContextProvider>
              </NotificationProvider>
            </AuthenticationContextProvider>
          </ApolloProvider>
        </ThemeWrapper>
      </CompatRouter>
    </BrowserRouter>
  );
}

function Router() {
  const { instruqtUser } = useAuthenticationContext();

  return (
    <Suspense fallback={<LoadingImage />}>
      <Switch>
        <CompatRoute path="/logout" exact render={() => <Logout />} />

        <CompatRoute path="/arcade">
          <ArcadeApp />
        </CompatRoute>

        {/* Redirect from old paths to new paths */}
        {['/study-room/invites/:inviteID/:teamSlug/tracks/:trackSlug', '/study-room/invites/:inviteID'].map(path => (
          <CompatRoute path={path} key={path}>
            <StudyRoomLinkRedirect />
          </CompatRoute>
        ))}

        <Route
          path={[
            '/embed',
            '/embedv2',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug/challenges',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug/recap',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug/completion',
            '/:teamSlug/tracks/:trackSlug/challenges',
            '/:teamSlug/tracks/:trackSlug/recap',
            '/:teamSlug/tracks/:trackSlug/completion',
          ]}
          component={PlayApp}
        />
        <Route
          exact
          path={[
            '/login',
            '/signup',
            '/public',
            '/users/self/edit',
            '/forgot-password',
            '/inspiration-library',
            '/inspiration-library/:templateSlug/:trackSlug',
            '/inspiration-library/tutorial-tracks/:trackSlug',
            '/:teamSlug/inspiration-library/:templateSlug/:trackSlug',
            '/:teamSlug/inspiration-library/tutorial-tracks/:trackSlug',
            '/claim-username',
            '/study-room',
            '/:teamSlug/invite/:inviteID/tracks/:trackSlug',
            '/:teamSlug/invite/:inviteID',
            '/:teamSlug/tracks/:trackSlug',
            '/not-found',
          ]}
          component={CoreApp}
        />
        <Route
          path={[
            '/create-track',
            '/:teamSlug/tracks/:trackSlug/wizard',
            '/:teamSlug/tracks/:trackSlug/editor',
            '/:teamSlug/tracks/:trackSlug/dashboard',
            '/:teamSlug/play-activity-report',
          ]}
          component={CreateApp}
        />
        <Route
          exact
          path={[
            '/:teamSlug/invites/:inviteID/live-event/:userID/activity',
            '/:teamSlug/invites/:inviteID/live-event/:userID/inspect',
            '/:teamSlug/invites/:inviteID/live-event',
          ]}
          component={ClassroomApp}
        />
        <Route
          exact
          path={[
            '/:teamSlug/statistics',
            '/:teamSlug/members',
            '/:teamSlug/arcades',
            '/:teamSlug/arcades/:arcadeID',
            '/:teamSlug/api-keys',
            '/:teamSlug/webhooks',
            '/:teamSlug/hot-start/create',
            '/:teamSlug/hot-start/:hotStartPoolID/details',
            '/:teamSlug/hot-start/:poolId/edit',
            '/:teamSlug/hot-start',
            '/:teamSlug/secrets',
            '/:teamSlug/limits',
            '/:teamSlug/invites',
            '/:teamSlug/invites/new',
            '/:teamSlug/invites/:inviteID',
            '/:teamSlug/invites/:inviteID/edit',
            '/:teamSlug/invites/:inviteID/duplicate',
            '/:teamSlug/invites/:inviteID/activity-report',
            '/:teamSlug/sandboxes',
            '/:teamSlug/sandbox-presets/add',
            '/:teamSlug/sandbox-presets/preview/:configID',
            '/:teamSlug/sandbox-presets/edit/:configID',
            '/:teamSlug/sandbox-presets/',
            '/:teamSlug/inspiration-library',
          ]}
          component={CreateApp}
        />
        <Route exact path={['/:teamSlug/invite/:inviteID']} component={CoreApp} />
        <Route path="/toy-shop" component={ToyApp} />
        <Route
          exact
          path={['/', '/:slug']}
          component={instruqtUser?.segments?.includes(UserSegment.Learner) ? CoreApp : CreateApp}
        />
        <Redirect to="/not-found" />
      </Switch>
    </Suspense>
  );
}

export interface AppContainerProps {
  children?: React.ReactNode;
}

export const AppContainer = ({ children }: AppContainerProps) => {
  const { instruqtUser, isAnonymous } = useAuthenticationContext();
  const location = useLocation();

  // Identify the current user in Sentry / Hubspot
  useEffect(() => {
    if (!isAnonymous) {
      trackUser(instruqtUser);
    } else {
      trackAnonUser();
    }
  }, [instruqtUser, isAnonymous]);

  // Track page views in Firebase / Google Analytics / Hubspot
  useEffect(() => {
    trackPageView(instruqtUser, location);
  }, [instruqtUser, location]);

  return (
    <Sentry.ErrorBoundary showDialog>
      <div tw="flex flex-col min-h-screen w-full">{children}</div>
    </Sentry.ErrorBoundary>
  );
};

const cache = new InMemoryCache({
  // The organization type has two denormalized relations, theme and features. Since these relations are one to one,
  // we can safely assume that properties across queries can be merged into one object. Apollo does not make this
  // assumption about denromalized relations and as such would invalidate cache entries for every new property queried on theme or features.
  typePolicies: {
    Organization: {
      fields: {
        theme: {
          merge: true,
        },
        features: {
          merge: true,
        },
      },
    },
    Team: {
      fields: {
        theme: {
          merge: true,
        },
        features: {
          merge: true,
        },
      },
    },
    User: {
      fields: {
        profile: {
          merge: true,
        },
      },
    },
  },
});

const httpLink = new HttpLink({
  uri: '/graphql',
});

const httpLinkToy = new HttpLink({
  uri: '/toygraphql',
});

const client = new ApolloClient({
  cache,
  link: ApolloLink.from([
    new SentryLink({ attachBreadcrumbs: { includeError: true } }),
    auth.embedTokenLink,
    auth.authLink,
    ApolloLink.split(o => o.getContext().clientName === 'toy', httpLinkToy, httpLink),
  ]),
  connectToDevTools: true,
  resolvers: {
    Mutation: {
      updateLocalOrganization: (_, { id, organization }, { cache }) => {
        cache.writeFragment({
          id: cache.identify({
            __typename: 'Organization',
            id,
          }),
          fragment: gql`
            fragment BrandedOrganization on Organization {
              name
              logo
              title
              subtitle
              slug

              primary_link_text
              primary_link_url
              secondary_link_text
              secondary_link_url

              theme {
                foreground_color
                foreground_image
                background_color
                background_image
                primary_foreground_color
                primary_background_color
                secondary_foreground_color
                secondary_background_color
              }
            }
          `,
          data: organization,
        });

        return organization;
      },
    },
  },
});
